import React, { useEffect, useMemo, useState } from 'react';
import { BankStatement, BankStatementRow, Debt, Transaction } from '../types';
import { defaultRowClassification, GENERIC_IMPORT_CATEGORIES } from '../lib/csvStatementParser';
import { AlertTriangle, Check, Landmark, Sparkles } from 'lucide-react';
import type { AccountOption } from './ReconcileDashboard';
import { suggestCategories } from '../lib/suggestCategories';

interface StatementReviewTableProps {
  statement: BankStatement;
  /** Set only for a CSV/PDF statement matched to a single account. Unset
   *  for a Plaid sync, whose rows can each belong to a different account --
   *  the header text below falls back to resolving those from `accountOptions`. */
  accountName?: string;
  /** Every bank account/debt the household has, so each row -- Plaid rows
   *  especially -- can resolve its own account by id instead of relying on
   *  a single statement-wide account. */
  accountOptions: AccountOption[];
  rows: BankStatementRow[];
  transactions: Transaction[];
  /** Resolves each row's suggestedDebtId (from findDebtPaymentMatches) to a
   *  real debt name for the confirm-suggestion banner below. */
  debts: Debt[];
  onApprove: (selections: { rowId: string; include: boolean; category: string; description: string; confirmDebtId?: string }[]) => Promise<void>;
}

const STATUS_BADGE: Record<BankStatementRow['status'], { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-emerald-100 text-emerald-700' },
  duplicate: { label: 'Duplicate', className: 'bg-slate-200 text-slate-600' },
  possible_duplicate: { label: 'Possible duplicate', className: 'bg-amber-100 text-amber-700' },
  transfer_candidate: { label: 'Transfer', className: 'bg-purple-100 text-purple-700' },
  excluded: { label: 'Excluded', className: 'bg-slate-100 text-slate-400' },
  imported: { label: 'Imported', className: 'bg-blue-100 text-blue-700' },
};

export default function StatementReviewTable({ statement, accountName, accountOptions, rows, transactions, debts, onApprove }: StatementReviewTableProps) {
  // Pending rows only that stay reviewable -- imported/excluded rows from a
  // prior commit (whether committed via Approve & Import with the box
  // unchecked, or from an earlier session) are already done and shown
  // read-only below instead, never re-submitted. Previously this only
  // filtered out 'imported', so an already-excluded row kept reappearing
  // here as an editable, DEFAULT-CHECKED pending row every time this table
  // re-rendered -- harmless in practice (commitStatementImport's own status
  // check silently no-ops an already-'excluded' row regardless of what the
  // checkbox sends) but genuinely misleading to look at.
  // transfer_candidate rows are handled entirely by the household-wide
  // "Matched Transfers" panel (a transfer's two legs live in two different
  // statements by definition, so they can't be approved from just one of
  // this statement's own tables) -- they disappear from here once matched
  // and reappear here, settled, once that panel's Confirm marks them
  // imported.
  const pendingRows = useMemo(() => rows.filter(r => r.status !== 'imported' && r.status !== 'excluded' && r.status !== 'transfer_candidate'), [rows]);
  const transferCandidateCount = useMemo(() => rows.filter(r => r.status === 'transfer_candidate').length, [rows]);
  const settledRows = useMemo(() => rows.filter(r => r.status === 'imported' || r.status === 'excluded'), [rows]);

  // `rows` almost always arrives empty on this component's first render --
  // it comes from a Firestore subscription in the parent that hasn't fired
  // yet at mount time -- so a lazy useState initializer here would compute
  // `included` against zero rows and then never run again, leaving every
  // real row unchecked and the Approve button permanently disabled once
  // real data does arrive. Found live, in an actual browser: a plain
  // Node-side check of the same logic can't catch this, since it's purely
  // about React's mount-vs-async-data timing. Fixed with an effect that
  // merges in a default for any row id not already present, without
  // clobbering a choice the user already made.
  const [expandedDuplicateRowId, setExpandedDuplicateRowId] = useState<string | null>(null);
  const [included, setIncluded] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setIncluded(prev => {
      let changed = false;
      const next = { ...prev };
      for (const r of pendingRows) {
        if (!(r.id! in next)) {
          next[r.id!] = r.status !== 'duplicate';
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pendingRows]);
  const [categoryOverrides, setCategoryOverrides] = useState<Record<string, string>>({});
  // AI-suggested categories (suggestRowCategories) -- opt-in via the
  // "Suggest categories" button rather than run automatically on load, so
  // a statement the user mostly skips never silently spends a Gemini call.
  // Only ever prefills the already-editable category input below; the
  // user's own Approve & Import click is still what actually commits it.
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, string>>({});
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Debt-payment suggestions are opt-in per row -- unchecked by default, so
  // a suggested match never silently reclassifies money without the user
  // explicitly confirming it. Reset per-statement is unnecessary since row
  // ids are globally unique.
  const [confirmedDebtRowIds, setConfirmedDebtRowIds] = useState<Record<string, boolean>>({});
  const debtById = useMemo(() => new Map(debts.map(d => [d.id, d])), [debts]);

  // A CSV/PDF statement is matched to exactly one account, so every row
  // shares statement.accountType. A Plaid sync leaves that null and relies
  // on each row's own accountId/accountType instead (set at sync time).
  const resolveAccount = (row: BankStatementRow) =>
    accountOptions.find(a => a.accountType === row.accountType && a.accountId === row.accountId);

  // Distinct accounts actually present across this statement's rows --
  // for a CSV/PDF statement this is always exactly the one matched account;
  // for a multi-account Plaid sync it can be several.
  const distinctAccountNames = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) {
      const acc = resolveAccount(r);
      if (acc) names.add(acc.name);
    }
    return Array.from(names);
  }, [rows, accountOptions]);

  const headerAccountText = accountName
    ? accountName
    : distinctAccountNames.length === 1
      ? distinctAccountNames[0]
      : distinctAccountNames.length > 1
        ? `${distinctAccountNames.length} linked accounts`
        : 'an unresolved account';

  const summary = useMemo(() => {
    let willImport = 0;
    let willSkip = 0;
    for (const r of pendingRows) {
      if (included[r.id!]) willImport++; else willSkip++;
    }
    return { willImport, willSkip };
  }, [pendingRows, included]);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const selections = pendingRows.map(r => {
        const confirmDebtId = r.suggestedDebtId && confirmedDebtRowIds[r.id!] ? r.suggestedDebtId : undefined;
        const defaultCategory = confirmDebtId
          ? `Payment: ${debtById.get(confirmDebtId)?.name || 'Debt'}`
          : (aiSuggestions[r.id!] || defaultRowClassification(r.direction, r.accountType).category);
        return {
          rowId: r.id!,
          include: !!included[r.id!],
          category: categoryOverrides[r.id!] || defaultCategory,
          description: r.description,
          confirmDebtId,
        };
      });
      await onApprove(selections);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuggestCategories = async () => {
    setSuggesting(true);
    setSuggestError(null);
    try {
      const existingCategories = [...new Set(
        transactions.map(t => t.category).filter(c => c && !GENERIC_IMPORT_CATEGORIES.has(c))
      )];
      const suggestions = await suggestCategories(
        pendingRows.map(r => ({ rowId: r.id!, description: r.description, amount: r.amount, direction: r.direction })),
        existingCategories
      );
      setAiSuggestions(prev => ({ ...prev, ...suggestions }));
    } catch (err: any) {
      setSuggestError(err.message || 'Could not get category suggestions.');
    } finally {
      setSuggesting(false);
    }
  };

  if (pendingRows.length === 0 && settledRows.length === 0 && transferCandidateCount === 0) {
    return <p className="text-sm text-slate-400 py-4">No transactions parsed from this statement.</p>;
  }

  if (pendingRows.length === 0 && settledRows.length === 0) {
    return (
      <p className="text-sm text-purple-600 py-4">
        Every transaction in this statement matched a transfer on another account — see
        "Matched Transfers" above to confirm or unpair them.
      </p>
    );
  }

  return (
    <div className="mt-3 border border-slate-100 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
        <p className="text-xs text-slate-500">
          Reviewing against <span className="font-semibold text-slate-700">{headerAccountText}</span> —
          {' '}{summary.willImport} to import, {summary.willSkip} skipped
          {transferCandidateCount > 0 && <> · {transferCandidateCount} matched as a transfer (see above)</>}
        </p>
        {pendingRows.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSuggestCategories}
              disabled={suggesting}
              title="Ask AI to suggest a category per row, based on each description and your own existing categories"
              className="flex items-center gap-1.5 bg-white text-slate-600 border border-slate-200 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {suggesting ? 'Suggesting…' : 'Suggest categories'}
            </button>
            <button
              onClick={handleApprove}
              disabled={submitting || summary.willImport === 0}
              className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
              {submitting ? 'Importing…' : `Approve & Import (${summary.willImport})`}
            </button>
          </div>
        )}
      </div>
      {suggestError && (
        <p className="px-4 py-1.5 text-xs text-rose-600 bg-rose-50 border-b border-rose-100">{suggestError}</p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2 font-medium w-8">
                <input
                  type="checkbox"
                  checked={pendingRows.length > 0 && pendingRows.every(r => included[r.id!])}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setIncluded(prev => {
                      const next = { ...prev };
                      for (const r of pendingRows) {
                        next[r.id!] = isChecked;
                      }
                      return next;
                    });
                  }}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  title="Select / deselect all"
                />
              </th>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Description</th>
              {distinctAccountNames.length > 1 && <th className="px-3 py-2 font-medium">Account</th>}
              <th className="px-3 py-2 font-medium">Category</th>
              <th className="px-3 py-2 font-medium text-right">Amount</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {pendingRows.map(row => {
              const badge = STATUS_BADGE[row.status];
              const isIncluded = !!included[row.id!];
              const defaultCategory = defaultRowClassification(row.direction, row.accountType).category;
              const rowAccountName = resolveAccount(row)?.name;
              return (
                <React.Fragment key={row.id}>
                <tr className={`border-b border-slate-50 last:border-0 ${!isIncluded ? 'opacity-40' : ''}`}>
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={isIncluded}
                      onChange={(e) => setIncluded(prev => ({ ...prev, [row.id!]: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                    {row.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td className="px-3 py-2 text-slate-800 max-w-[220px] truncate" title={row.description}>{row.description}</td>
                  {distinctAccountNames.length > 1 && (
                    <td className="px-3 py-2 text-slate-500 text-xs max-w-[160px] truncate" title={rowAccountName}>{rowAccountName || '—'}</td>
                  )}
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={categoryOverrides[row.id!] ?? aiSuggestions[row.id!] ?? defaultCategory}
                      onChange={(e) => setCategoryOverrides(prev => ({ ...prev, [row.id!]: e.target.value }))}
                      className="w-full px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                  <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${row.direction === 'credit' ? 'text-emerald-600' : 'text-slate-700'}`}>
                    {row.direction === 'credit' ? '+' : '−'}${row.amount.toFixed(2)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                      {row.status === 'possible_duplicate' && <AlertTriangle className="w-3 h-3" />}
                      {badge.label}
                    </span>
                    {row.duplicateOfTxIds && row.duplicateOfTxIds.length > 0 && (
                      <button
                        onClick={() => setExpandedDuplicateRowId(expandedDuplicateRowId === row.id ? null : row.id!)}
                        className="ml-2 text-[10px] font-semibold text-blue-600 hover:underline"
                      >
                        {expandedDuplicateRowId === row.id ? 'Hide' : 'View'}
                      </button>
                    )}
                  </td>
                </tr>
                {row.suggestedDebtId && debtById.has(row.suggestedDebtId) && (
                  <tr key={`${row.id}-debt-suggestion`} className="bg-indigo-50/60 border-b border-slate-100">
                    <td colSpan={distinctAccountNames.length > 1 ? 7 : 6} className="px-4 py-2">
                      <label className="flex items-center gap-2 text-xs text-indigo-900 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!confirmedDebtRowIds[row.id!]}
                          onChange={(e) => setConfirmedDebtRowIds(prev => ({ ...prev, [row.id!]: e.target.checked }))}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                        <Landmark className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                        <span>
                          Looks like a payment toward <span className="font-semibold">{debtById.get(row.suggestedDebtId)?.name}</span> --
                          confirm to record it as a debt payment instead of a plain expense.
                        </span>
                      </label>
                    </td>
                  </tr>
                )}
                {expandedDuplicateRowId === row.id && row.duplicateOfTxIds && (
                  <tr key={`${row.id}-duplicates`} className="bg-slate-50 border-b border-slate-100">
                    <td colSpan={distinctAccountNames.length > 1 ? 7 : 6} className="px-4 py-3">
                      <div className="text-xs text-slate-600 mb-2 font-medium">Matching existing transactions:</div>
                      <div className="space-y-2">
                        {transactions.filter(t => row.duplicateOfTxIds!.includes(t.id!)).map(dupTx => (
                          <div key={dupTx.id} className="flex items-center gap-4 bg-white p-2 border border-slate-200 rounded shadow-sm">
                            <span className="text-slate-500 text-xs w-20">{dupTx.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            <span className="text-slate-800 text-xs flex-1 truncate">{dupTx.description}</span>
                            <span className="text-slate-500 text-xs w-32 truncate">{dupTx.category}</span>
                            <span className={`text-xs font-medium ${dupTx.type === 'income' || dupTx.type === 'debt_payment' ? 'text-emerald-600' : 'text-slate-700'}`}>
                              {dupTx.type === 'income' || dupTx.type === 'debt_payment' ? '+' : '−'}${dupTx.amount.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
            {settledRows.map(row => {
              const isImported = row.status === 'imported';
              return (
                <tr key={row.id} className={`border-b border-slate-50 last:border-0 ${isImported ? 'bg-blue-50/30' : 'bg-slate-50/50 opacity-60'}`}>
                  <td className="px-4 py-2">{isImported && <Check className="w-4 h-4 text-blue-500" />}</td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                    {row.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td className="px-3 py-2 text-slate-500 max-w-[220px] truncate" title={row.description}>{row.description}</td>
                  {distinctAccountNames.length > 1 && (
                    <td className="px-3 py-2 text-slate-400 text-xs max-w-[160px] truncate">{resolveAccount(row)?.name || '—'}</td>
                  )}
                  <td className="px-3 py-2 text-slate-400 text-xs">—</td>
                  <td className="px-3 py-2 text-right text-slate-500 whitespace-nowrap">
                    {row.direction === 'credit' ? '+' : '−'}${row.amount.toFixed(2)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[row.status].className}`}>
                      {isImported && <Check className="w-3 h-3" />}
                      {STATUS_BADGE[row.status].label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
